import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, Modal, PageHeader, Row, Table,} from 'antd';
import {ReloadOutlined, SearchOutlined, SyncOutlined} from "@ant-design/icons";
import _ from "lodash";
import {getD365ExpenseTypes, syncD365ExpenseTypes} from "../../api/d365Api";
import moment from "moment/moment";
import {PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {useUser} from "../../auth/userHooks";
import {handleResponse} from "./utils";
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";

const ExpenseTypes = () => {
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [filteredExpenseTypes, setFilteredExpenseTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [syncDataRowSource, setSyncDataRowSource] = useState([]);
  const [syncDataSourceLeft, setSyncDataSourceLeft] = useState([]);
  const [syncDataSourceRight, setSyncDataSourceRight] = useState([]);
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const columns = [
    {
      title: 'Name',
      key: 'ID',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: i => i ? i : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      sorter: (a, b) => a.CreatedAt.localeCompare(b.CreatedAt),
      width: '225px',
      render: i => i ? moment.utc(i).local().format(PREFERRED_DATETIME_FORMAT) : '-'
    },
  ];
  const syncDataColumns = [
    {
      dataIndex: 'name'
    }, {
      dataIndex: 'value',
      align: 'right'
    }
  ];
  const syncDataRowsColumns = [
    {
      title: 'Action',
      dataIndex: 'action',
      width: 55
    }, {
      title: 'Name',
      dataIndex: 'name'
    }, {
      title: 'Type',
      dataIndex: 'type',
      width: 135
    }, {
      title: 'UID',
      dataIndex: 'uid',
      align: 'right',
      width: 180
    }, {
      title: 'ID',
      dataIndex: 'id',
      align: 'right',
      width: 90
    }

  ]

  const filterExpenseTypes = (expenseTypes, searchText) => {
    setFilteredExpenseTypes(
      _.chain(expenseTypes)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.name), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    filterExpenseTypes(expenseTypes, text);
  };

  const loadExpenseTypes = async () => {
    try {
      setLoading(true);
      const expenseTypes = await getD365ExpenseTypes();
      setExpenseTypes(expenseTypes);
      filterExpenseTypes(expenseTypes)
    } catch (e) {
      console.error(`Could not get expense types`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenseTypes();
    // eslint-disable-next-line
  }, []);

  const sync = async () => {
    setLoading(true);
    const response = await syncD365ExpenseTypes();
    handleResponse(response, "Expense Type", "sync");
    try {
      if (response.ok) {
        response.json().then(r => {
          setSyncDataSourceLeft([
            {
              name: 'Expense Types read from Projector',
              value: r.readProjector
            }, {
              name: 'Expense Types read from Database',
              value: r.readDB
            }, {
              name: 'Expense Types equal',
              value: r.equal
            }, {
              name: 'Expense Types updated in DB',
              value: r.updated
            }]);
          setSyncDataSourceRight([
            {
              name: 'Expense Types created in DB',
              value: r.created
            }, {
              name: 'Expense Types deleted in DB',
              value: r.deleted
            }, {
              name: 'Expense Mappings deleted in DB',
              value: r.deletedMapping
            }
          ]);
          setSyncDataRowSource(r.msg);
          setShowModal(true);
          loadExpenseTypes();
        });
      }
    } catch (e) {
      console.error(`Could not get expense type sync data`);
    } finally {
      setLoading(false);
    }
  }


  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Expense Types"
            subTitle={`(${filteredExpenseTypes.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={16} style={{marginRight: 10}}>
          <Input
            style={{maxWidth: 400}}
            placeholder="Search for expense types by name"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto">
          <Button
            style={{float: 'right'}}
            onClick={() => loadExpenseTypes()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {user?.permissions?.canManageExpenses && (
            <Button style={{float: 'right', marginRight: 8}} onClick={() => sync()} loading={loading}
                    icon={<SyncOutlined/>}>
              Synchronize from Projector
            </Button>
          )}
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredExpenseTypes}
        columns={columns}
        pagination={getPaginationConfig(filteredExpenseTypes.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
      <Modal
        title={`Sync Results`}
        open={showModal}
        onOk={() => setShowModal(false)}
        onCancel={() => setShowModal(false)}
        cancelButtonProps={{style: {display: 'none'}}}
        width={1000}
      >
        <Row>
          <Col span={11}>
            <Table
              dataSource={syncDataSourceLeft}
              columns={syncDataColumns}
              size="small"
              pagination={false}
              showHeader={false}
            />
          </Col>
          <Col span={2}>&nbsp;</Col>
          <Col span={11}>
            <Table
              dataSource={syncDataSourceRight}
              columns={syncDataColumns}
              size="small"
              pagination={false}
              showHeader={false}
            />
          </Col>
        </Row>
        <br/><br/>
        <Table
          dataSource={syncDataRowSource}
          columns={syncDataRowsColumns}
          pagination={getPaginationConfig(syncDataRowSource.length)}
          size="small"
        />
      </Modal>
    </>
  );
};

export {ExpenseTypes};
